import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { EnterpriseEnums } from 'src/app/enum/enterprise-enums';
import { EnterpriseModel } from 'src/app/models/management/enterprise.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-attach-images-enterprise',
  templateUrl: './attach-images-enterprise.component.html',
  styleUrls: ['./attach-images-enterprise.component.scss'],
  standalone: false
})
export class AttachImagesEnterpriseComponent implements OnInit {

  disabledForm: boolean = false;
  enterpriseContract: EnterpriseModel = new EnterpriseModel();
  imageTitles: string[] = ['Logo', 'Logo Footer', 'Firma'];
  permissionUpdatable: boolean = false;
  previews: (SafeUrl | null)[] = [null, null, null];
  subscription: Subscription;

  constructor(private confirmDialogService: ConfirmDialogService,
    public config: DynamicDialogConfig,
    private enterpriseService: EnterpriseService,
    private sanitizer: DomSanitizer,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.enterpriseContract = inputs.enterpriseContract;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.disabledForm = inputs.disabledForm;
    });
    await this.chargeImages();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async attachImage(image: File, slotIndex: number) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para modificar empresas.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    const configMap: Record<number, { successMessage: string }> = {
      [EnterpriseEnums.logo]: {
        successMessage: 'El logo se subió correctamente.',
      },
      [EnterpriseEnums.logoFooter]: {
        successMessage: 'El logo del footer se subió correctamente.',
      },
      [EnterpriseEnums.signature]: {
        successMessage: 'La firma se subió correctamente.',
      },
    };
    const config = configMap[slotIndex];
    const formData = new FormData();
    formData.append('file', image);
    try {
      const enterpriseId = this.enterpriseContract.enterpriseId;
      this.enterpriseContract = await firstValueFrom(this.enterpriseService.attachImage(enterpriseId,
        slotIndex, formData));
      this.emitEnterpriseContract(this.enterpriseContract);
      this.toastHandlerService.showToast('success', config.successMessage);
    } catch (error) {
      console.error('Error al subir la imagen.', error);
      this.toastHandlerService.showToast('error', 'Error al subir la imagen.');
    }
  }

  async attachImageUrl(imageUrl: string, slotIndex: number) {
    try {
      this.previews[slotIndex - 1] = imageUrl;
    } catch (error) {
      this.toastHandlerService.showToast('error', 'Error al subir la imagen.', '');
      console.error(error);
    }
  }

  async buildImageUrl(imagePath: string): Promise<SafeUrl | null> {
    try {
      const blob = await firstValueFrom(this.enterpriseService.getImageUrl(imagePath));
      const objectURL = URL.createObjectURL(blob);
      return this.sanitizer.bypassSecurityTrustUrl(objectURL);
    } catch (error) {
      console.error("Error cargando la imagen.", error);
      return null;
    }
  }

  async chargeImages() {
    const imagePaths = [
      this.enterpriseContract.logo,
      this.enterpriseContract.logoFooter,
      this.enterpriseContract.signature
    ];

    for (let i = 0; i < imagePaths.length; i++) {
      if (imagePaths[i]) {
        this.previews[i] = await this.buildImageUrl(imagePaths[i]!);
      }
    }
  }

  async deleteImage(slotIndex: number) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para modificar empresas.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    const configMap: Record<number, { message: string; successMessage: string }> = {
      [EnterpriseEnums.logo]: {
        message: '¿Realmente desea limpiar el logo?',
        successMessage: 'El logo se eliminó correctamente.',
      },
      [EnterpriseEnums.logoFooter]: {
        message: '¿Realmente desea limpiar el logo del footer?',
        successMessage: 'El logo del footer se eliminó correctamente.',
      },
      [EnterpriseEnums.signature]: {
        message: '¿Realmente desea limpiar la firma?',
        successMessage: 'La firma se eliminó correctamente.',
      },
    };
    const config = configMap[slotIndex];
    if (config) {
      if (await this.confirmDialogService.confirm(config.message)) {
        try {
          const enterpriseId = this.enterpriseContract.enterpriseId;
          this.enterpriseContract = await firstValueFrom(this.enterpriseService.deleteImage(enterpriseId, slotIndex));
          this.previews[slotIndex - 1] = null;
          this.emitEnterpriseContract(this.enterpriseContract);
          this.toastHandlerService.showToast('success', config.successMessage);
        } catch (error) {
          console.error('Error al eliminar la imagen.', error);
        }
      }
    }
  }

  emitEnterpriseContract(enterpriseContract: EnterpriseModel) {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'updateEnterpriseContract', payload: enterpriseContract });
  }
}
